/** @format */
import { QuestionChangeRecord } from "@question-bank/shared";
import { useState, useEffect, type MouseEvent } from "react";
import { getQuestionChangeHistory } from "../api/questions";
import { useBrand } from "../hooks/useBrand";
import { formatChangeValue } from "../utils/helpers";

interface QuestionChangeHistoryProps {
	questionId: string;
	onClose: () => void;
}
export const AdminQuestionChangeHistoryModal = ({ questionId, onClose }: QuestionChangeHistoryProps) => {
	const [history, setHistory] = useState<QuestionChangeRecord[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const { brand } = useBrand();

	const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	useEffect(() => {
		const fetchHistory = async () => {
			try {
				setIsLoading(true);
				const response = await getQuestionChangeHistory(brand, questionId);

				setHistory(response);
			} catch (error) {
				setError(error instanceof Error ? error.message : "Error loading history");
			} finally {
				setIsLoading(false);
			}
		};
		fetchHistory();
	}, [questionId, brand]);

	if (isLoading) {
		return <div>Loading...</div>;
	}
	if (error) {
		return <div>Error: {error}</div>;
	}
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
			onMouseDown={handleBackdropMouseDown}
		>
			<div className="flex flex-col w-full max-w-176.25 max-h-[90vh] bg-white rounded-lg py-4 px-6 overflow-hidden">
				<div className="flex flex-row items-center justify-between mb-2">
					<h2 className="text-lg font-bold">Change History</h2>
					<button onClick={onClose} className="cursor-pointer">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2}
							stroke="currentColor"
							className="size-6 opacity-50"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="flex-1 overflow-y-auto min-h-100 flex flex-col justify-center">
					{history.length === 0 ? (
						<div className="h-full flex flex-col items-center justify-center">
							<p className="text-slate-700 text-center">No changes recorded yet.</p>
						</div>
					) : (
						<div className="space-y-4 overflow-y-auto shadow-(--admin-shadow-raised) rounded-lg">
							{[...history].reverse().map((record, index) => (
								<div key={index} className="bg-gray-100 px-4 py-2 break-all">
									<p className="text-lg font-semibold text-slate-700">{new Date(record.timestamp).toLocaleString()}</p>

									<ul className="mt-2 text-sm space-y-1">
										{record.changes.map((change, i) => (
											<li key={i} className="text-[16px] text-slate-700 mb-4">
												<p className="font-bold capitalize">{change.field}</p>
												<div className="ml-2 space-y-1">
													<p className="font-semibold">From:</p>
													<pre className="whitespace-pre-wrap wrap-break-word hyphens-auto">
														{formatChangeValue(change.oldValue)}
													</pre>
													<p className="font-semibold mt-2">To:</p>
													<pre className="whitespace-pre-wrap wrap-break-word hyphens-auto">
														{formatChangeValue(change.newValue)}
													</pre>
												</div>
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					)}
				</div>
				<div className="mt-4 flex justify-end">
					<button
						onClick={onClose}
						className="bg-(--admin-color-primary-strong) cursor-pointer text-white px-6 h-8 rounded-md font-semibold flex items-center justify-center"
					>
						Close
					</button>
				</div>
			</div>
		</div>
	);
};
